import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider } from '@azure/msal-react'
import * as Sentry from '@sentry/react'
import App from './App'
import { msalInstance } from './config/authConfig'
import './i18n/config'
import './index.css'

// Initialize Sentry for error tracking
const environment = import.meta.env.MODE
if (environment === 'production' || window.location.hostname.includes('stage')) {
  Sentry.init({
    dsn: 'https://f461569279380e37bd624ee724d801e7@o485672.ingest.us.sentry.io/4510719324061696',
    environment: window.location.hostname.includes('stage') ? 'staging' : 'production',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  })
}

// Record login to audit log using proper token acquisition
async function recordLogin() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'
    const account = msalInstance.getActiveAccount()
    if (!account) return
    
    // Acquire token silently for API - use idToken like api.ts does
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account,
    })
    
    const response = await fetch(`${apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${tokenResponse.idToken}`,
        'Content-Type': 'application/json',
      },
    })
    console.log('Login recorded:', response.status)
  } catch (error) {
    console.error('Failed to record login:', error)
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

// Initialize MSAL and render app
msalInstance.initialize().then(() => {
  // Handle redirect promise (don't block rendering)
  msalInstance.handleRedirectPromise().then(async (response) => {
    if (response) {
      msalInstance.setActiveAccount(response.account)
      // Record login to audit log
      await recordLogin()
    } else {
      // Set active account if already signed in
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      }
    }
  }).catch((error) => {
    console.error('Redirect error:', error)
  })

  // Render app immediately after MSAL initializes
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  )
})
